import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { CellProps } from 'react-table'
import { QUERIES } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  CustomTable,
  DataColumn,
  PlainHeader,
  StackedCell,
  TableColumn,
  TableEmptyState,
} from 'app/components'
import { rotuloDispositivo, rotuloMotivoFim } from '../../../dispositivos/core/rotulos'
import { TelekitDispositivoList } from '../../telekits-list/core/_models'
import { getAlocacoesTelekit } from '../../telekits-list/core/_requests'
import { useRecarregaAoFecharModal } from '../../../dispositivos/core/useRecarregaAoFecharModal'

/** Todas as alocações da maleta (ativas e encerradas), com período e motivo. */
export const TelekitHistoricoTab = ({ idTelekit }: { idTelekit: number }) => {
  const { t } = useT()
  const { data, isFetching, isError, refetch } = useQuery(
    [QUERIES.TELEKIT_ALOCACOES, idTelekit, 'todas'],
    () => getAlocacoesTelekit(idTelekit, 'sort=dtInicio,desc&page=0&size=200'),
    { keepPreviousData: true, refetchOnWindowFocus: false }
  )
  useRecarregaAoFecharModal(refetch)

  const colunas = useMemo<ReadonlyArray<TableColumn<TelekitDispositivoList>>>(
    () => [
      {
        Header: () => (
          <PlainHeader>{t('pagina.telekits.dispositivos.col.dispositivo')}</PlainHeader>
        ),
        id: 'dispositivo',
        Cell: ({ row }: CellProps<TelekitDispositivoList>) => (
          <StackedCell
            primary={rotuloDispositivo(t, row.original.dispositivo)}
            secondary={row.original.dispositivo.patrimonio ?? ''}
          />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.maletas.col.empresa')}</PlainHeader>,
        id: 'empresa',
        hideOnMobile: true,
        Cell: ({ row }: CellProps<TelekitDispositivoList>) => (
          <StackedCell primary={row.original.empresaAlocacao?.nome ?? ''} />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.maletas.col.inicio')}</PlainHeader>,
        id: 'dtInicio',
        cellWidth: '150px',
        Cell: ({ row }: CellProps<TelekitDispositivoList>) => (
          <DataColumn data={row.original.dtInicio ?? undefined} />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.maletas.col.fim')}</PlainHeader>,
        id: 'dtFim',
        cellWidth: '150px',
        Cell: ({ row }: CellProps<TelekitDispositivoList>) =>
          row.original.ativo ? (
            <StackedCell primary={t('pagina.dispositivos.maletas.atual')} />
          ) : (
            <DataColumn data={row.original.dtFim ?? undefined} />
          ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.maletas.col.motivo')}</PlainHeader>,
        id: 'motivoFim',
        cellWidth: '150px',
        Cell: ({ row }: CellProps<TelekitDispositivoList>) => (
          <StackedCell primary={rotuloMotivoFim(t, row.original.motivoFim)} />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.maletas.col.observacao')}</PlainHeader>,
        id: 'observacao',
        hideOnMobile: true,
        Cell: ({ row }: CellProps<TelekitDispositivoList>) => (
          <StackedCell primary={row.original.observacao ?? ''} />
        ),
      },
    ],
    [t]
  )

  return (
    <CustomTable<TelekitDispositivoList>
      id='kt_table_telekit_historico'
      ariaLabel={t('pagina.telekits.dispositivos.aba_historico')}
      data={data?.data ?? []}
      columns={colunas}
      isLoading={isFetching}
      isError={isError}
      onRetry={refetch}
      notPaginated
      emptyState={<TableEmptyState title={t('pagina.telekits.dispositivos.historico_vazio')} />}
    />
  )
}
